
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Phone, Edit3, Save, X } from "lucide-react";
import { Candidate, UpdateCandidateData } from "../types";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { updateCandidateBasicInfoSchema, UpdateCandidateBasicInfoSchema } from "../schemas/updateCandidateBasicInfoSchema";
import { useUpdateCandidateProfile } from "@/hooks/mutations/useUpdateCandidateProfile";
import { ScreeningDialog } from "../../screening/ScreeningDialog";

interface BasicInfoProps {
  candidate: Candidate;
}

export function BasicInfo({ candidate }: BasicInfoProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [showScreeningDialog, setShowScreeningDialog] = useState(false);
  const { updateCandidateProfile, isUpdatingProfile } = useUpdateCandidateProfile();

  const form = useForm<UpdateCandidateBasicInfoSchema>({
    resolver: zodResolver(updateCandidateBasicInfoSchema),
    defaultValues: {
      first_name: candidate.first_name || "",
      last_name: candidate.last_name || "",
    },
  });

  const onSubmit = (values: UpdateCandidateBasicInfoSchema) => {
    const dataToUpdate: UpdateCandidateData = {};
    if (values.first_name && values.first_name !== candidate.first_name) {
      dataToUpdate.first_name = values.first_name;
    }
    if (values.last_name && values.last_name !== candidate.last_name) {
      dataToUpdate.last_name = values.last_name;
    }

    if (Object.keys(dataToUpdate).length > 0) {
      updateCandidateProfile(
        { candidateId: candidate.id, data: dataToUpdate },
        {
          onSuccess: (updatedCandidate) => {
            setIsEditing(false);
            form.reset({
              first_name: updatedCandidate?.first_name || candidate.first_name || "",
              last_name: updatedCandidate?.last_name || candidate.last_name || "",
            });
          },
        }
      );
    } else {
      setIsEditing(false); // No changes, just exit edit mode
    }
  };

  const handleCancel = () => {
    form.reset({
      first_name: candidate.first_name || "",
      last_name: candidate.last_name || "",
    });
    setIsEditing(false);
  };
  
  useEffect(() => {
    form.reset({
      first_name: candidate.first_name || "",
      last_name: candidate.last_name || "",
    });
  }, [candidate, form]);


  return (
    <div className="space-y-4">
      {isEditing ? (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John" {...field} disabled={isUpdatingProfile} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Doe" {...field} disabled={isUpdatingProfile} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex items-center gap-2 justify-end">
              <Button type="button" variant="outline" size="sm" onClick={handleCancel} disabled={isUpdatingProfile}>
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button type="submit" size="sm" disabled={isUpdatingProfile}>
                <Save className="h-4 w-4 mr-1" /> {isUpdatingProfile ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </Form>
      ) : (
        <div className="flex items-start justify-between">
          <div>
            {/* Name was removed here, it's already in the main header */}
            {candidate.current_role && (
              <p className="text-sm text-muted-foreground mt-1">
                {candidate.current_role}
              </p>
            )}
             {/* Placeholder if no current role, to maintain some structure */}
            {!candidate.current_role && <div className="h-6"></div>}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              <Edit3 className="h-4 w-4 mr-2" />
              Edit Name
            </Button>
            <Button 
              variant="default"
              size="sm"
              onClick={() => setShowScreeningDialog(true)}
            >
              <Phone className="h-4 w-4 mr-2" />
              Screen Now
            </Button>
          </div>
        </div>
      )}

      <ScreeningDialog
        open={showScreeningDialog}
        onClose={() => setShowScreeningDialog(false)}
        candidate={candidate}
        jobTitle={candidate.current_role || "Software Developer"} 
      />
    </div>
  );
}
